/**
 * Frontend default category thresholds.
 *
 * ⚠️  IMPORTANT: The `name` field MUST exactly match the first element
 *    of the findings array produced by findingsGenerator.js, because
 *    getFindingStatus(categoryName, …) looks up thresholds by this key.
 */
export const defaultCategories = [
    // =========================================================
    // NETWORK SCAN  (from generateNetworkFindings)
    // =========================================================
    {
        name: 'Backups',
        value_type: 'numeric',
        description: 'Percentage of devices without recent backups.',
        default_thresholds: {
            Good: { operator: '<=', value: 20 },
            Moderate: { operator: 'Between', lower: 21, upper: 50 },
            Bad: { operator: '>', value: 50 }
        }
    },
    {
        name: 'Admins',
        value_type: 'numeric',
        description: 'Percentage of administrative accounts with no password expiration.',
        default_thresholds: {
            Good: { operator: '<=', value: 20 },
            Moderate: { operator: 'Between', lower: 21, upper: 50 },
            Bad: { operator: '>', value: 50 }
        }
    },
    {
        name: 'Accounts',
        value_type: 'numeric',
        description: 'Percentage of user accounts with non-expiring or weak passwords.',
        default_thresholds: {
            Good: { operator: '<=', value: 20 },
            Moderate: { operator: 'Between', lower: 21, upper: 50 },
            Bad: { operator: '>', value: 50 }
        }
    },
    {
        name: 'Antivirus',
        value_type: 'numeric',
        description: 'Percentage of devices missing or with outdated antivirus.',
        default_thresholds: {
            Good: { operator: '<=', value: 20 },
            Moderate: { operator: 'Between', lower: 21, upper: 50 },
            Bad: { operator: '>', value: 50 }
        }
    },
    {
        name: 'Encryption',
        value_type: 'numeric',
        description: 'Percentage of devices without disk encryption.',
        default_thresholds: {
            Good: { operator: '<=', value: 20 },
            Moderate: { operator: 'Between', lower: 21, upper: 50 },
            Bad: { operator: '>', value: 50 }
        }
    },
    {
        name: 'Firewall (Network Scan)',
        value_type: 'numeric',
        description: 'Percentage of network devices with firewalls disabled.',
        default_thresholds: {
            Good: { operator: '<=', value: 20 },
            Moderate: { operator: 'Between', lower: 21, upper: 50 },
            Bad: { operator: '>', value: 50 }
        }
    },
    {
        name: 'OS (Network Scan)',
        value_type: 'numeric',
        description: 'Percentage of network devices running end-of-life operating systems.',
        default_thresholds: {
            Good: { operator: '<=', value: 20 },
            Moderate: { operator: 'Between', lower: 21, upper: 50 },
            Bad: { operator: '>', value: 50 }
        }
    },
    {
        name: 'Services (Network Scan)',
        value_type: 'numeric',
        description: 'Percentage of network devices running suspicious or unnecessary services.',
        default_thresholds: {
            Good: { operator: '<=', value: 20 },
            Moderate: { operator: 'Between', lower: 21, upper: 50 },
            Bad: { operator: '>', value: 50 }
        }
    },
    {
        name: 'Open Ports (Network Scan)',
        value_type: 'numeric',
        description: 'Percentage of network devices with sensitive open ports exposed.',
        default_thresholds: {
            Good: { operator: '<=', value: 20 },
            Moderate: { operator: 'Between', lower: 21, upper: 50 },
            Bad: { operator: '>', value: 50 }
        }
    },
    {
        name: 'Drive',
        value_type: 'numeric',
        description: 'Percentage of drives nearing critical usage (>85%).',
        default_thresholds: {
            Good: { operator: '<=', value: 20 },
            Moderate: { operator: 'Between', lower: 21, upper: 50 },
            Bad: { operator: '>', value: 50 }
        }
    },
    {
        name: 'Age',
        value_type: 'numeric',
        description: 'Percentage of devices older than 5 years.',
        default_thresholds: {
            Good: { operator: '<=', value: 20 },
            Moderate: { operator: 'Between', lower: 21, upper: 50 },
            Bad: { operator: '>', value: 50 }
        }
    },
    {
        name: 'Softwares',
        value_type: 'numeric',
        description: 'Percentage of devices with outdated third-party applications.',
        default_thresholds: {
            Good: { operator: '<=', value: 20 },
            Moderate: { operator: 'Between', lower: 21, upper: 50 },
            Bad: { operator: '>', value: 50 }
        }
    },

    // =========================================================
    // LOCALHOST SCAN  (from generateLocalhostFindings)
    // =========================================================
    {
        name: 'Firewall (Localhost Scan)',
        value_type: 'numeric',
        description: 'Percentage of localhost firewall profiles that are disabled.',
        default_thresholds: {
            Good: { operator: '<=', value: 20 },
            Moderate: { operator: 'Between', lower: 21, upper: 50 },
            Bad: { operator: '>', value: 50 }
        }
    },
    {
        name: 'Services (Localhost Scan)',
        value_type: 'numeric',
        description: 'Percentage of suspicious non-default services on localhost.',
        default_thresholds: {
            Good: { operator: '<=', value: 20 },
            Moderate: { operator: 'Between', lower: 21, upper: 50 },
            Bad: { operator: '>', value: 50 }
        }
    },
    {
        name: 'OS (Localhost Scan)',
        value_type: 'numeric',
        description: 'Percentage of localhost OS versions that are outdated or end-of-life.',
        default_thresholds: {
            Good: { operator: '<=', value: 20 },
            Moderate: { operator: 'Between', lower: 21, upper: 50 },
            Bad: { operator: '>', value: 50 }
        }
    },
    {
        name: 'Open Ports (Localhost Scan)',
        value_type: 'numeric',
        description: 'Percentage of sensitive open ports found on localhost.',
        default_thresholds: {
            Good: { operator: '<=', value: 20 },
            Moderate: { operator: 'Between', lower: 21, upper: 50 },
            Bad: { operator: '>', value: 50 }
        }
    },

    // =========================================================
    // ACTIVE DIRECTORY SCAN  (from generateADFindings)
    // =========================================================
    {
        name: 'AD Computers',
        value_type: 'numeric',
        description: 'Percentage of inactive or disabled AD Computer accounts.',
        default_thresholds: {
            Good: { operator: '<=', value: 20 },
            Moderate: { operator: 'Between', lower: 21, upper: 50 },
            Bad: { operator: '>', value: 50 }
        }
    },
    {
        name: 'AD Users',
        value_type: 'numeric',
        description: 'Percentage of inactive AD User accounts.',
        default_thresholds: {
            Good: { operator: '<=', value: 20 },
            Moderate: { operator: 'Between', lower: 21, upper: 50 },
            Bad: { operator: '>', value: 50 }
        }
    },
    {
        name: 'OU',
        value_type: 'numeric',
        description: 'Percentage of OUs with risk issues (empty, sensitive names, disabled with members).',
        default_thresholds: {
            Good: { operator: '<=', value: 20 },
            Moderate: { operator: 'Between', lower: 21, upper: 50 },
            Bad: { operator: '>', value: 50 }
        }
    },
    {
        name: 'AD Groups',
        value_type: 'numeric',
        description: 'Percentage of unmanaged or empty AD security groups.',
        default_thresholds: {
            Good: { operator: '<=', value: 20 },
            Moderate: { operator: 'Between', lower: 21, upper: 50 },
            Bad: { operator: '>', value: 50 }
        }
    },
    {
        name: 'FSMO',
        value_type: 'numeric',
        description: 'Percentage of FSMO roles with high/medium risk issues.',
        default_thresholds: {
            Good: { operator: '<=', value: 20 },
            Moderate: { operator: 'Between', lower: 21, upper: 50 },
            Bad: { operator: '>', value: 50 }
        }
    },
    {
        name: 'GPO',
        value_type: 'numeric',
        description: 'Percentage of GPOs with configuration issues.',
        default_thresholds: {
            Good: { operator: '<=', value: 20 },
            Moderate: { operator: 'Between', lower: 21, upper: 50 },
            Bad: { operator: '>', value: 50 }
        }
    },

    // =========================================================
    // MS365 SCAN  (from generateMS365Findings)
    // =========================================================
    {
        name: 'MS Users',
        value_type: 'numeric',
        description: 'Percentage of inactive MS365 user accounts.',
        default_thresholds: {
            Good: { operator: '<=', value: 20 },
            Moderate: { operator: 'Between', lower: 21, upper: 50 },
            Bad: { operator: '>', value: 50 }
        }
    },
    {
        name: 'MFA',
        value_type: 'numeric',
        description: 'Percentage of users missing Multi-Factor Authentication.',
        default_thresholds: {
            Good: { operator: '<=', value: 20 },
            Moderate: { operator: 'Between', lower: 21, upper: 50 },
            Bad: { operator: '>', value: 50 }
        }
    },
    {
        name: 'MS Policy',
        value_type: 'numeric',
        description: 'Percentage of MS365 policies that are misconfigured or disabled.',
        default_thresholds: {
            Good: { operator: '<=', value: 20 },
            Moderate: { operator: 'Between', lower: 21, upper: 50 },
            Bad: { operator: '>', value: 50 }
        }
    },
    {
        name: 'Excessive Admin Privileges',
        value_type: 'numeric',
        description: 'Percentage of MS365 users with excessive admin privileges.',
        default_thresholds: {
            Good: { operator: '<=', value: 20 },
            Moderate: { operator: 'Between', lower: 21, upper: 50 },
            Bad: { operator: '>', value: 50 }
        }
    },
    {
        name: 'Excessive Sharing',
        value_type: 'numeric',
        description: 'Percentage of excessive sharing on OneDrive/SharePoint.',
        default_thresholds: {
            Good: { operator: '<=', value: 20 },
            Moderate: { operator: 'Between', lower: 21, upper: 50 },
            Bad: { operator: '>', value: 50 }
        }
    },
    {
        name: 'Data Retention Policy',
        value_type: 'numeric',
        description: 'Percentage of misconfigured data retention policies.',
        default_thresholds: {
            Good: { operator: '<=', value: 20 },
            Moderate: { operator: 'Between', lower: 21, upper: 50 },
            Bad: { operator: '>', value: 50 }
        }
    },
    {
        name: 'Storage Quota',
        value_type: 'numeric',
        description: 'Percentage of users with storage usage above 80%.',
        default_thresholds: {
            Good: { operator: '<=', value: 20 },
            Moderate: { operator: 'Between', lower: 21, upper: 50 },
            Bad: { operator: '>', value: 50 }
        }
    },
    {
        name: 'Endpoint Manager Policy',
        value_type: 'numeric',
        description: 'Percentage of unconfigured Endpoint Manager policies.',
        default_thresholds: {
            Good: { operator: '<=', value: 20 },
            Moderate: { operator: 'Between', lower: 21, upper: 50 },
            Bad: { operator: '>', value: 50 }
        }
    },

    // =========================================================
    //  HIBP / DARK WEB  (from generateHIBPFindings)
    // =========================================================
    {
        name: 'Dark Web',
        value_type: 'numeric',
        description: 'Percentage of accounts found in data breaches.',
        default_thresholds: {
            Good: { operator: '<=', value: 20 },
            Moderate: { operator: 'Between', lower: 21, upper: 50 },
            Bad: { operator: '>', value: 50 }
        }
    },

    // =========================================================
    //  PII SCAN  (from generatePIIFindings)
    // =========================================================
    {
        name: 'PII',
        value_type: 'numeric',
        description: 'Percentage of scanned items containing Personally Identifiable Information.',
        default_thresholds: {
            Good: { operator: '<=', value: 20 },
            Moderate: { operator: 'Between', lower: 21, upper: 50 },
            Bad: { operator: '>', value: 50 }
        }
    },

    // =========================================================
    // CUSTOM DASHBOARD GRID CATEGORIES (UI-only, not from findingsGenerator)
    // =========================================================
    {
        name: 'MFA Coverage',
        value_type: 'numeric',
        description: 'Percentage of total users protected by MFA.',
        default_thresholds: {
            Good: { operator: '>=', value: 90 },
            Moderate: { operator: 'Between', lower: 70, upper: 89 },
            Bad: { operator: '<', value: 70 }
        }
    },
    {
        name: 'Privileged Account Control',
        value_type: 'numeric',
        description: 'Score based on managed vs unmanaged privileged accounts.',
        default_thresholds: {
            Good: { operator: '>=', value: 90 },
            Moderate: { operator: 'Between', lower: 70, upper: 89 },
            Bad: { operator: '<', value: 70 }
        }
    },
    {
        name: 'Endpoint Protection Status',
        value_type: 'numeric',
        description: 'Percentage of endpoints with active protection.',
        default_thresholds: {
            Good: { operator: '>=', value: 90 },
            Moderate: { operator: 'Between', lower: 70, upper: 89 },
            Bad: { operator: '<', value: 70 }
        }
    },
    {
        name: 'Patch Compliance',
        value_type: 'numeric',
        description: 'Percentage of endpoints passing patch compliance scans.',
        default_thresholds: {
            Good: { operator: '>=', value: 90 },
            Moderate: { operator: 'Between', lower: 70, upper: 89 },
            Bad: { operator: '<', value: 70 }
        }
    },
    {
        name: 'Disk Encryption Coverage',
        value_type: 'numeric',
        description: 'Percentage of endpoints with disk encryption enabled.',
        default_thresholds: {
            Good: { operator: '>=', value: 90 },
            Moderate: { operator: 'Between', lower: 70, upper: 89 },
            Bad: { operator: '<', value: 70 }
        }
    },
    {
        name: 'Inactive & Orphaned Accounts',
        value_type: 'numeric',
        description: 'Number of orphaned or inactive accounts detected.',
        default_thresholds: {
            Good: { operator: '==', value: 0 },
            Moderate: { operator: 'Between', lower: 1, upper: 10 },
            Bad: { operator: '>', value: 10 }
        }
    },
    {
        name: 'Shared or Generic Accounts',
        value_type: 'numeric',
        description: 'Number of shared administrative or generic accounts.',
        default_thresholds: {
            Good: { operator: '==', value: 0 },
            Moderate: { operator: 'Between', lower: 1, upper: 5 },
            Bad: { operator: '>', value: 5 }
        }
    },
    {
        name: 'Password Policy Enforcement',
        value_type: 'numeric',
        description: 'Score representing strictness of password policies.',
        default_thresholds: {
            Good: { operator: '>=', value: 90 },
            Moderate: { operator: 'Between', lower: 70, upper: 89 },
            Bad: { operator: '<', value: 70 }
        }
    },
    {
        name: 'Unauthorized Software Presence',
        value_type: 'numeric',
        description: 'Number of unauthorized or high-risk applications found.',
        default_thresholds: {
            Good: { operator: '==', value: 0 },
            Moderate: { operator: 'Between', lower: 1, upper: 5 },
            Bad: { operator: '>', value: 5 }
        }
    }
];

export function evaluateThreshold(value, valueType, rules) {
    if (!rules || typeof rules !== 'object') {
        return 'Unknown';
    }

    if (valueType === 'boolean') {
        if (rules.Good && rules.Good.value === value) return 'Good';
        if (rules.Bad && rules.Bad.value === value) return 'Bad';
        if (rules.Moderate && rules.Moderate.value === value) return 'Moderate';
        return 'Unknown';
    }

    if (valueType === 'numeric') {
        const val = parseFloat(value);
        if (isNaN(val)) return 'Unknown';

        const tiers = ['Bad', 'Moderate', 'Good'];
        for (const tier of tiers) {
            const rule = rules[tier];
            if (!rule) continue;

            let matches = false;
            switch(rule.operator) {
                case '<': matches = val < rule.value; break;
                case '<=': matches = val <= rule.value; break;
                case '>': matches = val > rule.value; break;
                case '>=': matches = val >= rule.value; break;
                case '==': matches = val == rule.value; break;
                case 'Between': matches = val >= rule.lower && val <= rule.upper; break;
            }
            if (matches) return tier;
        }
    }
    
    return 'Unknown';
}
